import type { ReactElement, ReactNode } from 'react';
import { cloneElement, Fragment } from 'react';

import type { I18nFn } from './types/I18nFn';
import { I18nLocale } from './types/I18nLocale';

function sprintf(format: string, values: Record<string, string | number>) {
  return format.replace(/%\(([^)]+)\)(s|d)/g, (_, key) => {
    let value: string | number | undefined = values[key as keyof typeof values];
    if (typeof value !== 'string' && typeof value !== 'number')
      value = undefined;

    return String(value);
  });
}

function interpolateComponents(
  text: string,
  components: Record<string, ReactElement>,
) {
  const result: ReactNode[] = [];
  const regex = /\{\{(\w+)\/\}\}|\{\{(\w+)\}\}(.*?)\{\{\/\2\}\}/g;

  let lastIndex = 0;

  for (const match of text.matchAll(regex)) {
    if (match.index > 0) result.push(text.slice(lastIndex, match.index));

    const name = match[1] ?? match[2];
    const children = (() => {
      const inside = match[3];

      return inside && inside.match(regex)
        ? interpolateComponents(inside, components)
        : inside;
    })();

    const component = components[name];
    if (!component) return text;

    result.push(
      children
        ? cloneElement(components[name], {}, children)
        : components[name],
    );

    lastIndex = match.index + match[0].length;
  }

  result.push(text.slice(lastIndex));

  return (
    <>
      {result.map((item, index) => (
        <Fragment key={index}>{item}</Fragment>
      ))}
    </>
  );
}

function unboundI18nFn(
  this: I18nLocale,
  text: string,
  ...dataArguments: Record<string, unknown>[]
) {
  const translatedText: string = (() => {
    if (typeof text !== 'object')
      throw new Error(`Missing i18n match for ${text}`);

    return text[this];
  })();

  if (dataArguments.length === 0) return translatedText;

  const [data] = dataArguments;

  const keys = Object.keys(data);
  if (keys.length === 0) return translatedText;

  const args: Record<string, string | number> = {};
  const components: Record<string, ReactElement> = {};

  for (const key of keys) {
    const value = data[key];

    if (typeof value === 'string' || typeof value === 'number')
      args[key] = value;
    else if (
      translatedText.includes(`{{${key}}}`) ||
      translatedText.includes(`{{${key}/}}`)
    )
      components[key] = value as ReactElement;
  }

  const translatedTextWithArgs = sprintf(translatedText, args);

  return Object.keys(components).length === 0
    ? translatedTextWithArgs
    : interpolateComponents(translatedTextWithArgs, components);
}

const fns = {
  en: unboundI18nFn.bind(I18nLocale.EN) as I18nFn,
  es: unboundI18nFn.bind(I18nLocale.ES) as I18nFn,
} satisfies Record<I18nLocale, I18nFn>;

export function getI18n(locale: I18nLocale): {
  locale: I18nLocale;
  i18n: I18nFn;
} {
  return { locale, i18n: fns[locale] };
}
